// @flow

import _ from 'lodash';

import { RouterSelectors } from '@zapier/toolbox-redux-framework';
import { singular } from '@zapier/common-utils';

import * as CliApp from 'app/entities/CliApp';
import * as CliAppDefinition from 'app/entities/CliAppDefinition';
import * as EnvironmentVariable from 'app/entities/EnvironmentVariable';
import * as Zap from 'app/entities/Zap';
import {
  APP_ACTIONS,
  BASIC_AUTH_FIELDS,
  OAUTH2_DATA,
  SERVICE_ROUTES,
  SERVICE_TYPES,
  rootKey,
} from 'app/developer-v3/constants';
import {
  createBundleItem,
  getAppVersionUrl,
  getMetaFields,
  getServiceUrl,
  getTriggerBundle,
  isAuthSetupComplete,
  mergeFields,
} from 'app/developer-v3/utils';

import type {
  ActionSchema,
  AppForm,
  AppService,
  ServiceType,
} from 'app/developer-v3/types';
import type { State } from 'app/common/types';
import type { TriggerSchema } from 'app/developer-v3/platformSchema/trigger';

const { actions: ACTIONS, triggers: TRIGGERS } = SERVICE_ROUTES;
const { searchOrCreates: SEARCH_OR_CREATES } = SERVICE_TYPES;

export const currentAppId = (state: State) =>
  _.get(state, [rootKey, 'currentApp', 'id']);

export const currentAppVersion = (state: State) =>
  _.get(state, [rootKey, 'currentApp', 'version']) || '1.0.0';

export const cleanDefinition = (state: State) =>
  _.get(state, [rootKey, 'cleanDefinition']);

export const formFields = (state: State, formId: AppForm) =>
  _.get(state, [rootKey, 'formFields', formId], []);

export const currentActiveTestTab = (state: State) =>
  _.get(state, [rootKey, 'currentActiveTestTab'], 0);

export const currentTestRequest = (state: State) =>
  _.get(state, [rootKey, 'testRequest'], {});

export const currentTestLogs = (state: State) =>
  _.get(state, [rootKey, 'testLogs']);

export const hasUnsavedChanges = (state: State) =>
  _.get(state, [rootKey, 'hasUnsavedChanges'], false);

export const lifecycleHasStarted = (state: State): boolean =>
  _.get(state, [rootKey, 'versions', 'lifecycle', 'started']);
export const lifecycleHasFinished = (state: State): boolean =>
  _.get(state, [rootKey, 'versions', 'lifecycle', 'success']);
export const lifecycleErrorMessage = (state: State): string =>
  _.get(state, [rootKey, 'versions', 'lifecycle', 'errorMessage']);
export const lifecycleHasFailed = (state: State) =>
  Boolean(lifecycleErrorMessage(state));

export const currentAppDefinitionEntityId = (state: State) => {
  const appDefinition = CliAppDefinition.selectors.all.findByAppIdAndVersion(
    currentAppId(state),
    currentAppVersion(state),
    state
  );
  return CliAppDefinition.selectors.id(appDefinition);
};

export const currentDefinitionOverride = (state: State) => {
  const appDefinition = CliAppDefinition.selectors.all.findByAppIdAndVersion(
    currentAppId(state),
    currentAppVersion(state),
    state
  );
  return CliAppDefinition.selectors.definitionOverride(appDefinition);
};

export const currentAppEntity = (state: State) => {
  const entityId = currentAppId(state);
  return CliApp.selectors.all.entity(entityId, state);
};

export const currentAppDefinitionEntity = (state: State) => {
  const entityId = currentAppDefinitionEntityId(state);
  return CliAppDefinition.selectors.all.entity(entityId, state);
};

export const currentAppTitle = (state: State) => {
  const entity = currentAppEntity(state);
  return CliApp.selectors.title(entity);
};

export const currentSelectedApi = (state: State) => {
  const entity = currentAppDefinitionEntity(state);
  return CliAppDefinition.selectors.selectedApi(entity);
};

export const currentServiceKey = (
  type: ServiceType | typeof ACTIONS,
  state: State
) => {
  const serviceTypeKey = `${singular(type)}Key`;
  const { [serviceTypeKey]: serviceKey, actionKey } = RouterSelectors.getParams(
    state
  );

  return actionKey || serviceKey;
};

export const currentLegacyService = (
  type: ServiceType | typeof ACTIONS,
  state: State
) => {
  const serviceKey = currentServiceKey(type, state);
  const serviceType =
    type === ACTIONS ? RouterSelectors.getParam('actionType', state) : type;
  return _.get(currentDefinitionOverride(state), [
    'legacy',
    serviceType,
    serviceKey,
  ]);
};

export const currentLegacyTrigger = _.partial(currentLegacyService, TRIGGERS);
export const currentLegacyAction = _.partial(currentLegacyService, ACTIONS);
export const currentLegacySearchOrCreates = _.partial(
  currentLegacyService,
  SEARCH_OR_CREATES
);

export const currentService = (
  type: ServiceType | typeof ACTIONS,
  state: State
) => {
  const serviceKey = currentServiceKey(type, state);
  const serviceType =
    type === ACTIONS ? RouterSelectors.getParam('actionType', state) : type;
  return _.get(currentDefinitionOverride(state), [serviceType, serviceKey], {});
};

export const currentTrigger = _.partial(currentService, TRIGGERS);
export const currentAction = _.partial(currentService, ACTIONS);
export const currentSearchOrCreates = _.partial(
  currentService,
  SEARCH_OR_CREATES
);

export const currentServiceType = (state: State) => {
  const { serviceType, actionType } = RouterSelectors.getParams(state);

  return actionType || serviceType;
};

export const currentServiceInputFields = (
  type: ServiceType | typeof ACTIONS,
  state: State
) => _.get(currentService(type, state), ['operation', 'inputFields'], []);

export const appActions = (state: State) => {
  const currentOverride = currentDefinitionOverride(state);
  // $FlowFixMe - _.flatMap accepts an object _OR_ an array. Come on flow...
  return _.reduce(
    APP_ACTIONS,
    (actions, service) => {
      if (_.isEmpty(currentOverride[service])) return actions;

      actions[service] = _.map(currentOverride[service], action => action);

      return actions;
    },
    {}
  );
};

export const isCurrentAppDefinitionSaving = (state: State) => {
  const entity = currentAppDefinitionEntity(state);
  return CliAppDefinition.selectors.isSaving(entity);
};

export const hasCurrentAppDefinitionFailed = (state: State) => {
  const entity = currentAppDefinitionEntity(state);
  return CliAppDefinition.selectors.isFailure(entity);
};

export const isCurrentAppSaving = (state: State) => {
  const entity = currentAppEntity(state);
  return CliApp.selectors.isSaving(entity);
};

export const currentAppVersionEntities = (state: State) => {
  const appId = currentAppId(state);
  const appEntity = currentAppEntity(state);

  const versionNumbers: Array<string> = CliApp.selectors.versions(appEntity);
  if (!versionNumbers.length) {
    return [];
  }

  return versionNumbers
    .map(versionNumber => {
      return CliAppDefinition.selectors.all.findByAppIdAndVersion(
        appId,
        versionNumber,
        state
      );
    })
    .filter(Boolean);
};

export const availableTriggers = (state: State) => {
  const currentTriggerKey = RouterSelectors.getParam('triggerKey', state);
  const triggers = currentDefinitionOverride(state).triggers || {};

  return _.filter(triggers, trigger => trigger.key !== currentTriggerKey);
};

export const allServicesByType = (serviceType: ServiceType, state: State) => {
  return _.get(currentDefinitionOverride(state), serviceType, {});
};

export const currentServicesListUrl = (type: ServiceType, state: State) => {
  const appId = currentAppId(state);
  const versionNumber = currentAppVersion(state);
  const { actionType } = RouterSelectors.getParams(state);
  const serviceRoute = actionType ? ACTIONS : type;

  return `${getAppVersionUrl(appId, versionNumber)}/${serviceRoute}`;
};

export const currentServiceUrl = (type: ServiceType, state: State) => {
  const id = currentAppId(state);
  const version = currentAppVersion(state);
  const params = RouterSelectors.getParams(state);

  return getServiceUrl(type, { id, version, ...params });
};

export const currentAppAuthentication = (state: State) => {
  const appDefinitionOverride = currentDefinitionOverride(state);
  return _.get(appDefinitionOverride, ['authentication'], []);
};

export const currentAuthFields = (state: State) => {
  const appDefinitionOverride = currentDefinitionOverride(state);
  return _.get(appDefinitionOverride, ['authentication', 'fields'], []);
};

export const currentAuthType = (state: State) => {
  const appDefinitionOverride = currentDefinitionOverride(state);
  return _.get(appDefinitionOverride, ['authentication', 'type']);
};

export const versionEnvironmentVariables = (state: State) => {
  const vars = EnvironmentVariable.selectors.all.environmentVariablesForVersion(
    currentAppId(state),
    currentAppVersion(state),
    state
  );

  const mapped = vars.map(EnvironmentVariable.selectors.data);
  return mapped;
};

const getAuthBundleHander = type => {
  const authHandlers = {
    oauth2: (fields, methodName) => {
      const allOauthFields = mergeFields(fields, OAUTH2_DATA);
      const useInputData = [
        'authorizeUrl',
        'getAccessToken',
        'refreshAccessToken',
      ];

      const bundleKey = useInputData.includes(methodName)
        ? 'inputData'
        : 'authData';

      return { [bundleKey]: allOauthFields };
    },
    basic: fields => ({
      authData: mergeFields(fields, BASIC_AUTH_FIELDS),
    }),
    digest: fields => ({
      authData: mergeFields(fields, BASIC_AUTH_FIELDS),
    }),
    default: fields => ({ authData: fields }),
  };

  return authHandlers[type] || authHandlers.default;
};

const getAuthBundle = (requestType: string, state: State) => {
  const authType = currentAuthType(state);
  const userFields = currentAuthFields(state);
  const authHandler: (
    fields: Array<*>,
    requestType?: string
  ) => {} = getAuthBundleHander(authType);

  if (!authType) {
    return {};
  }

  return authHandler(userFields, requestType);
};

const getApiBundleHandler = (
  serviceType: ServiceType | typeof ACTIONS,
  state: State
) => {
  const { operation } = currentService(serviceType, state);

  const apiHandlers = {
    triggers: requestType =>
      getTriggerBundle(operation.type === 'polling', requestType),
    default: () => ({}),
  };

  // $FlowFixMe: We know that serviceType might not be in bundleHandlers, hence the default property.
  return apiHandlers[serviceType] || apiHandlers.default;
};

const getApiBundle = (serviceType: ServiceType) => (
  requestType: string,
  state: State
) => {
  const apiHandler: (requestType?: string) => {} = getApiBundleHandler(
    serviceType,
    state
  );
  return {
    inputData: currentServiceInputFields(serviceType, state),
    ...apiHandler(requestType),
    ...getAuthBundle('', state),
  };
};

const getBundleHandler = (serviceType: ServiceType | typeof ACTIONS) => {
  const bundleHandlers = {
    authentication: getAuthBundle,
    default: getApiBundle,
  };

  // $FlowFixMe: We know that serviceType might not be in bundleHandlers, hence the default property.
  return bundleHandlers[serviceType] || bundleHandlers.default(serviceType);
};

export const currentPlatformVariablesList = (
  serviceType: ServiceType | typeof ACTIONS,
  state: State
) => (requestType: string) => {
  const envVars = versionEnvironmentVariables(state).map(
    envVar => `process.env.${envVar.key}`
  );

  const addBundleFields = getBundleHandler(serviceType);

  const bundle = {
    meta: getMetaFields(requestType),
    ...addBundleFields(requestType, state),
  };

  const bundleVars = _.flatMap(bundle, (values, bundleKey) =>
    _.map(values, item => createBundleItem(bundleKey, item.key))
  );

  return [...envVars, ...bundleVars];
};

export const selectIsAuthSetupComplete = (state: State) => {
  const auth = currentAppAuthentication(state);

  return isAuthSetupComplete(auth);
};

export const selectIsServiceSetup = (state: State) => {
  const serviceType = currentServiceType(state);
  const service = currentService(serviceType, state);
  const { url, source } = _.get(service, ['operation', 'perform'], {});

  return !!(url || source);
};

export const selectTasks = (state: State) => {
  return _.get(state, [rootKey, 'tasks']);
};

type AppDefinition = {
  triggers?: TriggerSchema,
  creates?: ActionSchema,
  searches?: ActionSchema,
};

type FlowNode = {};

type ZapType = {
  id: number,
  nodes: Array<FlowNode>,
};

const matchZaps = (
  selectedApi: string,
  definition: AppDefinition,
  zaps: Array<ZapType>,
  types: Array<'triggers' | 'creates' | 'searches'>
) => {
  // $FlowFixMe - flatMap wants type object, JS Arrays are typeof objects :/
  return _.flatMap(types, type => {
    return (Object.entries(definition[type] || {}): any).map(
      ([key, def]: [string, AppService]) => {
        const matchedZap = zaps.find(zapEntity => {
          const nodes = Zap.selectors.nodes(zapEntity);
          return (
            !_.isEmpty(nodes) &&
            nodes.some(
              node => node.selected_api === selectedApi && node.action === key
            )
          );
        });

        return {
          key,
          name: def.display.label,
          zapId: matchedZap && Zap.selectors.id(matchedZap),
          type,
        };
      }
    );
  });
};

function makeCreateDetails(tasks) {
  const hasMatchingTask = zapId => tasks.some(task => task.root_id === zapId);

  return detail => ({
    key: detail.key,
    name: detail.name,
    hasZap: !!detail.zapId,
    hasTask: !!detail.zapId && hasMatchingTask(detail.zapId),
    type: detail.type,
  });
}

export const selectZapTaskDetails = (state: State) => {
  const selectedApi = currentSelectedApi(state);
  const definition = currentDefinitionOverride(state);
  const zaps = Zap.selectors.all.entities(state) || [];
  const createDetails = makeCreateDetails(selectTasks(state));

  const zapMap = {
    triggers: matchZaps(selectedApi, definition, zaps, ['triggers']),
    actions: matchZaps(selectedApi, definition, zaps, ['creates', 'searches']),
  };

  return {
    triggers: zapMap.triggers.map(createDetails),
    actions: zapMap.actions.map(createDetails),
  };
};

export const selectWbAppId = (state: State) => {
  return (
    _.get(state, ['developer', 'currentAppId']) ||
    _.get(state, ['developer', 'currentCliAppVersion'])
  );
};

export const selectAppIdOrWb = (state: State) => {
  const v3AppId = currentAppId(state);
  const wbId = selectWbAppId(state);

  return {
    appId: v3AppId || wbId,
    isWb: !!wbId,
  };
};

// A little confusing, but this is for when someone is viewing a v3 CLI app within WB.
// Can be removed once WB is deleted.
export const selectWbCliAppVersion = (state: State) => {
  _.get(state, ['developer', 'currentCliAppVersion']);
};

export const selectAppVersionOrWb = (state: State) => {
  return currentAppVersion(state) || selectAppVersionOrWb(state);
};
